import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ActiveSeasonsNotice from "../../../components/activeseasonsnotice/ActiveSeasonsNotice";
import styles from "./CommercialSummary.module.scss";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  Skeleton,
} from "antd";
import {
  ExportOutlined,
  CalendarOutlined,
  AlertOutlined,
  BulbOutlined,
  FlagOutlined,
  TeamOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  RiseOutlined,
  FallOutlined,
  ThunderboltOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import {
  getCommercialSummaryCore,
  getCommercialSummaryProducts,
  getCommercialSummaryClients,
  getAllChannels,
  getAllBranchOffices,
  getWholesaleFilters,
} from "../../../api/Services";
import {
  CommercialSummaryCore,
  CommercialSummaryProducts,
  CommercialSummaryClients,
  CommercialSummaryCategoryRow,
  CommercialSummaryStoreRow,
} from "../../../types/commercialSummary";
import VariationBadge from "../../../components/kpi/VariationBadge";
import KpiCard from "../../../components/kpi/KpiCard";
import {
  formatVariation,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;

interface Option {
  value: number;
  label: string;
}

const EMPTY_VALUE = "-";

const formatCurrency = (value: number) =>
  `$${(value ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;

const formatNumber = (value: number) => (value ?? 0).toLocaleString("es-AR");

const renderAmountVar = (amount: number, variation: number | null) => (
  <div>
    <div>{formatCurrency(amount)}</div>
    <VariationBadge value={variation} />
  </div>
);

const renderCountVar = (count: number, variation: number | null) => (
  <div>
    <div>{formatNumber(count)}</div>
    <VariationBadge value={variation} />
  </div>
);

const renderPctVar = (pct: number, variation: number | null) => (
  <div>
    <div>{(pct ?? 0).toFixed(1)}%</div>
    <VariationBadge value={variation} />
  </div>
);

type TFunction = (key: string) => string;

const numericSorter = (field: string) => (a: any, b: any) => (a[field] ?? 0) - (b[field] ?? 0);

const getMetricColumnDefs = (t: TFunction): Record<string, any> => ({
  sales: {
    title: t("CommercialSummary-Column-Sales"),
    key: "sales",
    align: "right" as const,
    sorter: numericSorter("sales"),
    render: (_: any, r: any) => renderAmountVar(r.sales, r.salesVar),
  },
  pctSales: {
    title: t("CommercialSummary-Column-PctSales"),
    dataIndex: "pctSales",
    key: "pctSales",
    align: "right" as const,
    sorter: numericSorter("pctSales"),
    render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
  },
  tickets: {
    title: t("CommercialSummary-Column-Tickets"),
    key: "tickets",
    align: "right" as const,
    sorter: numericSorter("tickets"),
    render: (_: any, r: any) => renderCountVar(r.tickets, r.ticketsVar),
  },
  units: {
    title: t("CommercialSummary-Column-Units"),
    key: "units",
    align: "right" as const,
    sorter: numericSorter("units"),
    render: (_: any, r: any) => renderCountVar(r.units, r.unitsVar),
  },
  averageTicket: {
    title: t("CommercialSummary-Column-AverageTicket"),
    key: "averageTicket",
    align: "right" as const,
    sorter: numericSorter("averageTicket"),
    render: (_: any, r: any) => renderAmountVar(r.averageTicket, r.averageTicketVar),
  },
  marginPct: {
    title: t("CommercialSummary-Column-Margin"),
    key: "marginPct",
    align: "right" as const,
    sorter: numericSorter("marginPct"),
    render: (_: any, r: any) => renderPctVar(r.marginPct, r.marginPctVar),
  },
});

const buildMetricColumns = (t: TFunction, order: string[]) => {
  const defs = getMetricColumnDefs(t);
  return order.map((key) => defs[key]);
};

const getChannelColumns = (t: TFunction) => [
  { title: t("CommercialSummary-Column-Channel"), dataIndex: "channelName", key: "channelName" },
  ...buildMetricColumns(t, ["sales", "pctSales", "tickets", "units", "averageTicket", "marginPct"]),
];

const getStoreColumns = (t: TFunction) => [
  { title: t("CommercialSummary-Column-Store"), dataIndex: "branchOfficeName", key: "branchOfficeName" },
  {
    title: t("CommercialSummary-Column-Channel"),
    dataIndex: "channelName",
    key: "channelName",
    render: (v: string | null) => v ?? EMPTY_VALUE,
  },
  ...buildMetricColumns(t, ["sales", "pctSales", "tickets", "units", "averageTicket", "marginPct"]),
];

const getCategoryColumns = (t: TFunction) => [
  {
    title: t("CommercialSummary-Column-Category"),
    dataIndex: "categoryName",
    key: "categoryName",
    render: (v: string | null) => v ?? EMPTY_VALUE,
  },
  ...buildMetricColumns(t, ["sales", "pctSales", "marginPct", "tickets", "averageTicket", "units"]),
];

const getProductColumns = (t: TFunction) => [
  {
    title: t("CommercialSummary-Column-Product"),
    key: "articleName",
    render: (_: any, r: any) => (
      <div>
        <div>{r.articleName}</div>
        {r.externalId && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.externalId}
          </Text>
        )}
      </div>
    ),
  },
  {
    title: t("CommercialSummary-Column-Sales"),
    dataIndex: "sales",
    key: "sales",
    align: "right" as const,
    sorter: numericSorter("sales"),
    render: (v: number) => formatCurrency(v),
  },
  {
    title: t("CommercialSummary-Column-TicketCount"),
    dataIndex: "tickets",
    key: "tickets",
    align: "right" as const,
    sorter: numericSorter("tickets"),
    render: (v: number) => formatNumber(v),
  },
  {
    title: t("CommercialSummary-Column-Units"),
    dataIndex: "units",
    key: "units",
    align: "right" as const,
    sorter: numericSorter("units"),
    render: (v: number) => formatNumber(v),
  },
  {
    title: t("CommercialSummary-Column-AverageTicket"),
    dataIndex: "averageTicket",
    key: "averageTicket",
    align: "right" as const,
    sorter: numericSorter("averageTicket"),
    render: (v: number) => formatCurrency(v),
  },
  {
    title: t("CommercialSummary-Column-Margin"),
    dataIndex: "marginPct",
    key: "marginPct",
    align: "right" as const,
    sorter: numericSorter("marginPct"),
    render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
  },
  {
    title: t("CommercialSummary-Column-PctSales"),
    dataIndex: "pctSales",
    key: "pctSales",
    align: "right" as const,
    sorter: numericSorter("pctSales"),
    render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
  },
];

const getSegmentColumns = (t: TFunction) => [
  { title: t("CommercialSummary-Column-Segment"), dataIndex: "segmentName", key: "segmentName" },
  {
    title: t("CommercialSummary-Column-Clients"),
    dataIndex: "clients",
    key: "clients",
    align: "right" as const,
    sorter: numericSorter("clients"),
    render: (v: number) => formatNumber(v),
  },
  {
    title: t("CommercialSummary-Column-Sales"),
    key: "sales",
    align: "right" as const,
    sorter: numericSorter("sales"),
    render: (_: any, r: any) => renderAmountVar(r.sales, r.salesVar),
  },
  {
    title: t("CommercialSummary-Column-PctSales"),
    dataIndex: "pctSales",
    key: "pctSales",
    align: "right" as const,
    sorter: numericSorter("pctSales"),
    render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
  },
];

const CAMPAIGN_TYPE_COLOR: Record<string, string> = {
  PROMOTION: "purple",
  INCENTIVE: "blue",
  SEGMENT: "green",
};

const getCampaignColumns = (t: TFunction) => [
  { title: t("CommercialSummary-Column-Campaign"), dataIndex: "name", key: "name" },
  {
    title: t("CommercialSummary-Column-Type"),
    dataIndex: "campaignType",
    key: "campaignType",
    render: (v: string) => (v ? <Tag color={CAMPAIGN_TYPE_COLOR[v] || "default"}>{v}</Tag> : EMPTY_VALUE),
  },
  {
    title: t("CommercialSummary-Column-Status"),
    dataIndex: "campaignStatus",
    key: "campaignStatus",
    render: (v: string) => (v ? <Tag>{v}</Tag> : EMPTY_VALUE),
  },
  {
    title: t("CommercialSummary-Column-Validity"),
    key: "validity",
    render: (_: any, r: any) =>
      `${r.validityStartDate ? moment(r.validityStartDate).format("DD/MM/YYYY") : EMPTY_VALUE} - ${
        r.validityEndDate ? moment(r.validityEndDate).format("DD/MM/YYYY") : EMPTY_VALUE
      }`,
  },
  {
    title: t("CommercialSummary-Column-ROI"),
    dataIndex: "roi",
    key: "roi",
    align: "right" as const,
    sorter: numericSorter("roi"),
    render: (v: number) => (v != null ? `${v.toFixed(1)}%` : EMPTY_VALUE),
  },
  {
    title: t("CommercialSummary-Column-IncrementalSales"),
    dataIndex: "incrementalSales",
    key: "incrementalSales",
    align: "right" as const,
    sorter: numericSorter("incrementalSales"),
    render: (v: number) => (v != null ? formatCurrency(v) : EMPTY_VALUE),
  },
  {
    title: t("CommercialSummary-Column-Cost"),
    dataIndex: "cost",
    key: "cost",
    align: "right" as const,
    sorter: numericSorter("cost"),
    render: (v: number) => (v != null ? formatCurrency(v) : EMPTY_VALUE),
  },
  {
    title: t("CommercialSummary-Column-Clients"),
    dataIndex: "clientCount",
    key: "clientCount",
    align: "right" as const,
    sorter: numericSorter("clientCount"),
    render: (v: number) => (v != null ? formatNumber(v) : EMPTY_VALUE),
  },
];

// ─── Section kept as mockup — real spec/backend pending (suggested focus) ───
const suggestedFocus = [
  { priority: "Alta", title: "Revisar caída sostenida en sandalias", description: "La categoría acumula 3 meses de caída consecutiva, con contribución negativa creciente. Evaluar si responde a estacionalidad o pérdida de mercado." },
  { priority: "Alta", title: "Reforzar eCommerce internacional", description: "El canal muestra crecimiento sostenido de +22%. Evaluar si hay capacidad de ampliar oferta y mejorar tiempos de entrega." },
  { priority: "Media", title: "Analizar clientes frecuentes en riesgo", description: "El segmento 'En riesgo' muestra caída del 8.3% en venta y agrupa 1.630 clientes. Considerar acciones de retención." },
  { priority: "Media", title: "Preparar stock de cinturones para próxima fecha comercial", description: "Stock actual alto con baja rotación. Evaluar promoción de liquidación antes del ingreso de nueva temporada." },
  { priority: "Baja", title: "Monitorear margen en promociones outlet", description: "El outlet cerró con margen del 22.1%, por debajo del umbral objetivo. Revisar profundidad de descuento." },
];

const priorityColor: Record<string, string> = {
  Alta: "red",
  Media: "orange",
  Baja: "blue",
};

const CommercialSummary: React.FC = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment]>([
    moment().startOf("month"),
    moment().endOf("month"),
  ]);
  const [channelId, setChannelId] = useState<number | undefined>(undefined);
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [brandId, setBrandId] = useState<number | undefined>(undefined);

  const [channelOptions, setChannelOptions] = useState<Option[]>([]);
  const [storeOptions, setStoreOptions] = useState<Option[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const [brandOptions, setBrandOptions] = useState<Option[]>([]);

  const [core, setCore] = useState<CommercialSummaryCore | null>(null);
  const [products, setProducts] = useState<CommercialSummaryProducts | null>(null);
  const [clients, setClients] = useState<CommercialSummaryClients | null>(null);
  const [loadingCore, setLoadingCore] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<moment.Moment | null>(null);

  useEffect(() => {
    getAllChannels().then((channels: any[]) => {
      setChannelOptions(
        (channels ?? []).map((c: any) => ({
          value: c.id,
          label: c.type?.descriptionEs ?? c.type?.type ?? String(c.id),
        }))
      );
    });
    getAllBranchOffices().then((stores: any[]) => {
      setStoreOptions((stores ?? []).map((s: any) => ({ value: s.id, label: s.name })));
    });
    getWholesaleFilters().then((filters) => {
      if (!filters) return;
      setCategoryOptions((filters.categories ?? []).map((c: any) => ({ value: c.id, label: c.name })));
      setBrandOptions((filters.brands ?? []).map((b: any) => ({ value: b.id, label: b.name })));
    });
  }, []);

  useEffect(() => {
    const params = {
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD"),
      channelId,
      branchOfficeId,
      categoryId,
      brandId,
    };

    setLoadingCore(true);
    getCommercialSummaryCore(params)
      .then((res) => {
        setCore(res);
        setLastUpdated(moment());
      })
      .finally(() => setLoadingCore(false));

    setLoadingProducts(true);
    getCommercialSummaryProducts(params)
      .then((res) => setProducts(res))
      .finally(() => setLoadingProducts(false));

    setLoadingClients(true);
    getCommercialSummaryClients(params)
      .then((res) => setClients(res))
      .finally(() => setLoadingClients(false));
  }, [dateRange, channelId, branchOfficeId, categoryId, brandId]);

  const kpis = core?.kpis;
  const periodStatus = core?.periodStatus;
  const clientKpis = clients?.clientKpis;
  const allAlerts = [...(core?.alerts ?? []), ...(products?.alerts ?? [])];
  const loadingAlerts = loadingCore || loadingProducts;

  const noData = t("CommercialSummary-NoData");
  const joinNames = (names: string[]) => (names && names.length > 0 ? names.join(", ") : noData);

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const appendSheet = (rows: any[] | undefined, name: string) => {
      if (!rows || rows.length === 0) return;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
    };

    appendSheet(
      core?.channels.map((r) => ({
        [t("CommercialSummary-Column-Channel")]: r.channelName,
        [t("CommercialSummary-Column-Sales")]: r.sales,
        [t("CommercialSummary-Column-SalesLY")]: r.salesLY,
        [t("CommercialSummary-Column-Variation")]: r.salesVar ?? variationNotAvailableLabel(),
        [t("CommercialSummary-Column-PctSales")]: r.pctSales,
        [t("CommercialSummary-Column-Tickets")]: r.tickets,
        [t("CommercialSummary-Column-Units")]: r.units,
        [t("CommercialSummary-Column-AverageTicket")]: r.averageTicket,
        [t("CommercialSummary-Column-Margin")]: r.marginPct,
      })),
      "Channels"
    );
    appendSheet(
      core?.stores.map((r: CommercialSummaryStoreRow) => ({
        [t("CommercialSummary-Column-Store")]: r.branchOfficeName,
        [t("CommercialSummary-Column-Channel")]: r.channelName,
        [t("CommercialSummary-Column-Sales")]: r.sales,
        [t("CommercialSummary-Column-SalesLY")]: r.salesLY,
        [t("CommercialSummary-Column-Variation")]: r.salesVar ?? variationNotAvailableLabel(),
        [t("CommercialSummary-Column-PctSales")]: r.pctSales,
        [t("CommercialSummary-Column-Tickets")]: r.tickets,
        [t("CommercialSummary-Column-Units")]: r.units,
        [t("CommercialSummary-Column-AverageTicket")]: r.averageTicket,
        [t("CommercialSummary-Column-Margin")]: r.marginPct,
      })),
      "Stores"
    );
    const categoryRowExport = (r: CommercialSummaryCategoryRow) => ({
      [t("CommercialSummary-Column-Category")]: r.categoryName,
      [t("CommercialSummary-Column-Sales")]: r.sales,
      [t("CommercialSummary-Column-SalesLY")]: r.salesLY,
      [t("CommercialSummary-Column-Variation")]: r.salesVar ?? variationNotAvailableLabel(),
      [t("CommercialSummary-Column-PctSales")]: r.pctSales,
      [t("CommercialSummary-Column-Margin")]: r.marginPct,
      [t("CommercialSummary-Column-Tickets")]: r.tickets,
      [t("CommercialSummary-Column-AverageTicket")]: r.averageTicket,
      [t("CommercialSummary-Column-Units")]: r.units,
    });
    appendSheet(core?.categoriesPositive.map(categoryRowExport), "Categories +");
    appendSheet(core?.categoriesNegative.map(categoryRowExport), "Categories -");
    const productRowExport = (r: any) => ({
      [t("CommercialSummary-Column-Product")]: r.articleName,
      [t("CommercialSummary-Column-ExternalId")]: r.externalId,
      [t("CommercialSummary-Column-Sales")]: r.sales,
      [t("CommercialSummary-Column-Tickets")]: r.tickets,
      [t("CommercialSummary-Column-Units")]: r.units,
      [t("CommercialSummary-Column-AverageTicket")]: r.averageTicket,
      [t("CommercialSummary-Column-Margin")]: r.marginPct,
      [t("CommercialSummary-Column-PctSales")]: r.pctSales,
    });
    appendSheet(products?.topProducts.map(productRowExport), "Top products");
    appendSheet(products?.bottomProducts.map(productRowExport), "Bottom products");
    appendSheet(
      clients?.segments.map((r) => ({
        [t("CommercialSummary-Column-Segment")]: r.segmentName,
        [t("CommercialSummary-Column-Clients")]: r.clients,
        [t("CommercialSummary-Column-Sales")]: r.sales,
        [t("CommercialSummary-Column-SalesLY")]: r.salesLY,
        [t("CommercialSummary-Column-Variation")]: r.salesVar ?? variationNotAvailableLabel(),
        [t("CommercialSummary-Column-PctSales")]: r.pctSales,
      })),
      "Segments"
    );
    appendSheet(
      clients?.campaigns.map((r) => ({
        [t("CommercialSummary-Column-Campaign")]: r.name,
        [t("CommercialSummary-Column-Type")]: r.campaignType,
        [t("CommercialSummary-Column-Status")]: r.campaignStatus,
        [t("CommercialSummary-Column-ValidityStart")]: r.validityStartDate,
        [t("CommercialSummary-Column-ValidityEnd")]: r.validityEndDate,
        [t("CommercialSummary-Column-ROI")]: r.roi,
        [t("CommercialSummary-Column-IncrementalSales")]: r.incrementalSales,
        [t("CommercialSummary-Column-Cost")]: r.cost,
        [t("CommercialSummary-Column-Clients")]: r.clientCount,
      })),
      "Campaigns"
    );

    XLSX.writeFile(
      wb,
      `resumen-comercial-${dateRange[0].format("YYYYMMDD")}-${dateRange[1].format("YYYYMMDD")}.xlsx`
    );
  };

  return (
    <div className={styles.wrapper}>
      {/* ════════ HEADER ════════ */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Title level={3} className={styles.pageTitle}>
            {t("CommercialSummary-Title")}
          </Title>
          <Text type="secondary" className={styles.lastUpdated}>
            {lastUpdated
              ? t("CommercialSummary-LastUpdated", { date: lastUpdated.format("DD/MM/YYYY HH:mm") })
              : t("CommercialSummary-Loading")}
          </Text>
        </div>
        <Button
          icon={<ExportOutlined />}
          type="default"
          onClick={handleExport}
          disabled={loadingCore || loadingProducts || loadingClients || (!core && !products && !clients)}
        >
          {t("CommercialSummary-Export")}
        </Button>
      </div>

      {/* ════════ FILTERS ════════ */}
      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("CommercialSummary-Filter-Period")}</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates: any) => dates && setDateRange(dates)}
              format="DD/MM/YYYY"
              allowClear={false}
              className={styles.datePicker}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("CommercialSummary-Filter-Channel")}</Text>
            <Select
              placeholder={t("CommercialSummary-Filter-Channel-Placeholder")}
              allowClear
              className={styles.filterSelect}
              options={channelOptions}
              value={channelId}
              onChange={(v) => setChannelId(v)}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("CommercialSummary-Filter-Store")}</Text>
            <Select
              placeholder={t("CommercialSummary-Filter-Store-Placeholder")}
              allowClear
              className={styles.filterSelect}
              options={storeOptions}
              value={branchOfficeId}
              onChange={(v) => setBranchOfficeId(v)}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("CommercialSummary-Filter-Category")}</Text>
            <Select
              placeholder={t("CommercialSummary-Filter-Category-Placeholder")}
              allowClear
              className={styles.filterSelect}
              options={categoryOptions}
              value={categoryId}
              onChange={(v) => setCategoryId(v)}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("CommercialSummary-Filter-Brand")}</Text>
            <Select
              placeholder={t("CommercialSummary-Filter-Brand-Placeholder")}
              allowClear
              className={styles.filterSelect}
              options={brandOptions}
              value={brandId}
              onChange={(v) => setBrandId(v)}
            />
          </div>
        </div>
      </Card>

      {/* ════════ KPIs ════════ */}
      {loadingCore ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <div className={styles.kpiRow}>
          {kpis && [
            { label: t("CommercialSummary-Kpi-GrossSales"), value: formatCurrency(kpis.grossSales.value), variation: kpis.grossSales.variationPct, icon: <ShoppingCartOutlined /> },
            { label: t("CommercialSummary-Kpi-Units"), value: formatNumber(kpis.units.value), variation: kpis.units.variationPct, icon: <TagOutlined /> },
            { label: t("CommercialSummary-Kpi-Tickets"), value: formatNumber(kpis.tickets.value), variation: kpis.tickets.variationPct, icon: <CalendarOutlined /> },
            { label: t("CommercialSummary-Kpi-AverageTicket"), value: formatCurrency(kpis.averageTicket.value), variation: kpis.averageTicket.variationPct, icon: <RiseOutlined /> },
            { label: t("CommercialSummary-Kpi-Margin"), value: `${kpis.marginPct.value.toFixed(1)}%`, variation: kpis.marginPct.variationPct, icon: <FlagOutlined /> },
          ].map((kpi, idx) => (
            <KpiCard
              key={idx}
              className={styles.kpiCard}
              icon={kpi.icon}
              iconClassName={styles.kpiIcon}
              label={kpi.label}
              labelClassName={styles.kpiLabel}
              value={kpi.value}
              valueClassName={styles.kpiValue}
              variation={kpi.variation}
              vsLabel={t("CommercialSummary-Kpi-VsLastYear")}
              vsLabelClassName={styles.kpiVsLabel}
            />
          ))}
        </div>
      )}

      {/* ════════ PERIOD STATUS ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <CalendarOutlined />
            <span>{t("CommercialSummary-PeriodStatus-Title")}</span>
          </Space>
        }
      >
        {loadingCore ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          periodStatus && (
            <Paragraph className={styles.periodSummary}>
              {t("CommercialSummary-PeriodStatus-Text", {
                grossSales: formatCurrency(periodStatus.grossSales),
                variation: formatVariation(periodStatus.grossSalesVar),
                growthStores: joinNames(periodStatus.growthStores),
                growthCategories: joinNames(periodStatus.growthCategories),
                declineCategories: joinNames(periodStatus.declineCategories),
                declineStores: joinNames(periodStatus.declineStores),
              })}
            </Paragraph>
          )
        )}
      </Card>

      {/* ════════ CHANNELS ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <RiseOutlined />
            <span>{t("CommercialSummary-Channels-Title")}</span>
          </Space>
        }
      >
        {loadingCore ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            columns={getChannelColumns(t)}
            dataSource={core?.channels ?? []}
            rowKey={(r: any) => String(r.channelId ?? r.channelName)}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            scroll={{ x: "max-content" }}
            className={styles.dataTable}
          />
        )}
      </Card>

      {/* ════════ STORES ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>{t("CommercialSummary-Stores-Title")}</span>
          </Space>
        }
      >
        {loadingCore ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            columns={getStoreColumns(t)}
            dataSource={core?.stores ?? []}
            rowKey={(r: any) => String(r.branchOfficeId)}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            scroll={{ x: "max-content" }}
            className={styles.dataTable}
          />
        )}
      </Card>

      {/* ════════ CATEGORIES ════════ */}
      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <ArrowUpOutlined style={{ color: "#52c41a" }} />
                <span>{t("CommercialSummary-CategoriesPositive-Title")}</span>
              </Space>
            }
          >
            {loadingCore ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <Table
                columns={getCategoryColumns(t)}
                dataSource={core?.categoriesPositive ?? []}
                rowKey={(r: any) => String(r.categoryId)}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                scroll={{ x: "max-content" }}
                className={styles.dataTable}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <ArrowDownOutlined style={{ color: "#f5222d" }} />
                <span>{t("CommercialSummary-CategoriesNegative-Title")}</span>
              </Space>
            }
          >
            {loadingCore ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <Table
                columns={getCategoryColumns(t)}
                dataSource={core?.categoriesNegative ?? []}
                rowKey={(r: any) => String(r.categoryId)}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                scroll={{ x: "max-content" }}
                className={styles.dataTable}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* ════════ PRODUCTS ════════ */}
      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <ThunderboltOutlined style={{ color: "#52c41a" }} />
                <span>{t("CommercialSummary-Products-TopTitle")}</span>
              </Space>
            }
          >
            {loadingProducts ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <Table
                columns={getProductColumns(t)}
                dataSource={products?.topProducts ?? []}
                rowKey={(r: any) => String(r.articleId)}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                scroll={{ x: "max-content" }}
                className={styles.dataTable}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <FallOutlined style={{ color: "#f5222d" }} />
                <span>{t("CommercialSummary-Products-BottomTitle")}</span>
              </Space>
            }
          >
            <ActiveSeasonsNotice />
            {loadingProducts ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <Table
                columns={getProductColumns(t)}
                dataSource={products?.bottomProducts ?? []}
                rowKey={(r: any) => String(r.articleId)}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                scroll={{ x: "max-content" }}
                className={styles.dataTable}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* ════════ CLIENTS ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <TeamOutlined />
            <span>{t("CommercialSummary-Clients-Title")}</span>
          </Space>
        }
      >
        {loadingClients ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : (
          clientKpis && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {[
                { label: t("CommercialSummary-Client-Unique"), value: formatNumber(clientKpis.uniqueClients.value), variation: clientKpis.uniqueClients.variationPct },
                { label: t("CommercialSummary-Client-New"), value: formatNumber(clientKpis.newClients.value), variation: clientKpis.newClients.variationPct },
                { label: t("CommercialSummary-Client-AverageTicket"), value: formatCurrency(clientKpis.averageTicketPerClient.value), variation: clientKpis.averageTicketPerClient.variationPct },
                { label: t("CommercialSummary-Client-Frequency"), value: clientKpis.averageFrequency.value.toFixed(2), variation: clientKpis.averageFrequency.variationPct },
              ].map((kpi, idx) => (
                <Col xs={24} sm={12} md={6} key={idx}>
                  <div className={styles.clientKpiBox}>
                    <Text className={styles.clientKpiLabel}>{kpi.label}</Text>
                    <div className={styles.clientKpiValue}>{kpi.value}</div>
                    <VariationBadge value={kpi.variation} />
                  </div>
                </Col>
              ))}
            </Row>
          )
        )}

        <Title level={5} style={{ marginBottom: 12 }}>
          {t("CommercialSummary-Segments-Title")}
        </Title>
        {loadingClients ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            columns={getSegmentColumns(t)}
            dataSource={clients?.segments ?? []}
            rowKey={(r: any) => String(r.segmentId)}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            scroll={{ x: "max-content" }}
            className={styles.dataTable}
          />
        )}
      </Card>

      {/* ════════ CAMPAIGNS ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <TagOutlined />
            <span>{t("CommercialSummary-Campaigns-Title")}</span>
          </Space>
        }
      >
        {loadingClients ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            columns={getCampaignColumns(t)}
            dataSource={clients?.campaigns ?? []}
            rowKey={(r: any) => String(r.id)}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            scroll={{ x: "max-content" }}
            className={styles.dataTable}
          />
        )}
      </Card>

      {/* ════════ SUGGESTED FOCUS (mockup — spec pending) ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <FlagOutlined />
            <span>{t("CommercialSummary-SuggestedFocus-Title")}</span>
          </Space>
        }
      >
        <div className={styles.focusList}>
          {suggestedFocus.map((focus, idx) => (
            <div key={idx} className={styles.focusItem}>
              <Tag color={priorityColor[focus.priority]} className={styles.focusTag}>
                {focus.priority}
              </Tag>
              <div className={styles.focusContent}>
                <Text strong>{focus.title}</Text>
                <Text type="secondary" className={styles.focusDescription}>
                  {focus.description}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ════════ ALERTS + DRIVERS ════════ */}
      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <AlertOutlined style={{ color: "#f5222d" }} />
                <span>{t("CommercialSummary-Alerts-Title")}</span>
              </Space>
            }
          >
            {loadingAlerts ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <div className={styles.bulletList}>
                {allAlerts.length === 0 ? (
                  <Text type="secondary">{t("CommercialSummary-Alerts-Empty")}</Text>
                ) : (
                  allAlerts.map((alert, idx) => (
                    <div key={idx} className={styles.bulletItem}>
                      <AlertOutlined className={styles.bulletRed} />
                      <div>
                        <Text>{t(alert.key, alert.params)}</Text>
                        {alert.params.products && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {alert.params.products}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <ThunderboltOutlined style={{ color: "#1890ff" }} />
                <span>{t("CommercialSummary-Drivers-Title")}</span>
              </Space>
            }
          >
            {loadingCore ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <div className={styles.bulletList}>
                {!core || core.drivers.length === 0 ? (
                  <Text type="secondary">{t("CommercialSummary-Drivers-Empty")}</Text>
                ) : (
                  core.drivers.map((driver, idx) => (
                    <div key={idx} className={styles.bulletItem}>
                      <ThunderboltOutlined className={styles.bulletBlue} />
                      <Text>{t(driver.key, driver.params)}</Text>
                    </div>
                  ))
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ════════ INSIGHTS ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <BulbOutlined style={{ color: "#8b5cf6" }} />
            <span>{t("CommercialSummary-Insights-Title")}</span>
          </Space>
        }
      >
        {loadingCore ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          <div className={styles.insightsList}>
            {!core || core.insights.length === 0 ? (
              <Text type="secondary">{t("CommercialSummary-Insights-Empty")}</Text>
            ) : (
              core.insights.map((insight, idx) => (
                <div key={idx} className={`${styles.insightItem} ${styles.insightGreen}`}>
                  <Text>{t(insight.key, insight.params)}</Text>
                </div>
              ))
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CommercialSummary;
